import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { axiosInstance } from "@/src_new/shared/lib/axiosInstance";
import { showToast } from "@/src_new/shared/lib/toast";

import { App_Url_New } from "@/src_new/shared/common/static";
import { setCustomerAuth, setRetailAuth } from "@/src_new/shared/redux/reducers/authSlice";
import {
  getClientIdFromVerifyResponse,
  hydrateResumeJourneyState,
  type ResumeSessionResponse,
  type ResumeTarget,
} from "../../utils/resumeJourney";
import LoginModal from "./LoginModal";
import UserLoginForm from "./UserLoginForm";

const MAX_INVALID_OTP_ATTEMPTS = 3;

type OtpApiError = {
  message?: string;
  detail?: string;
};

type MessageType = "success" | "error" | "info";

type OtpResumeContext = {
  target: ResumeTarget;
  clientId: string;
  verifyResponse: unknown;
};

type OtpResumeLoginProps = {
  isOpen: boolean;
  onClose: () => void;
  showUserLoginForm?: boolean;
  emailAddress?: string;
  phoneNumber?: string;
  fallbackClientId?: string | null;
  initialMessage?: string;
  title?: string;
  validationType?: "email" | "phone" | "auto";
  autoRedirect?: boolean;
  renderInline?: boolean;
  onSessionResolved?: (
    sessionData: ResumeSessionResponse,
    context: OtpResumeContext
  ) => void | Promise<void>;
};

const buildOtpBody = (identifier: string, type: "email" | "phone", otp?: string) => {
  if (type === "email") {
    return otp ? { email: identifier, otp } : { email: identifier };
  }

  const payload = {
    phone_number: identifier,
    mobile_number: identifier,
    phone: identifier,
  };

  if (!otp) return payload;
  return { ...payload, otp };
};

const fetchResumeSession = async (clientId: string, token: string) => {
  const getSessionResponse = await axiosInstance.get<ResumeSessionResponse>(
    `${App_Url_New.secured.backlinks.get_session}${clientId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return getSessionResponse.data;
};

const OtpResumeLogin = ({
  isOpen,
  onClose,
  showUserLoginForm = false,
  emailAddress,
  phoneNumber,
  fallbackClientId,
  initialMessage,
  title,
  validationType = "auto",
  autoRedirect = true,
  renderInline = false,
  onSessionResolved,
}: OtpResumeLoginProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(initialMessage || "");
  const [messageType, setMessageType] = useState<MessageType>("info");
  const [invalidOtpAttempts, setInvalidOtpAttempts] = useState(0);

  const resetMessageState = useCallback(() => {
    setMessage(initialMessage || "");
    setMessageType("info");
    setInvalidOtpAttempts(0);
  }, [initialMessage]);

  useEffect(() => {
    if (!isOpen) return;
    resetMessageState();
  }, [isOpen, resetMessageState]);

  const getApiErrorMessage = useCallback((error: unknown, fallback: string) => {
    const axiosError = error as AxiosError<OtpApiError>;
    return (
      axiosError.response?.data?.detail ||
      axiosError.response?.data?.message ||
      (error instanceof Error ? error.message : fallback)
    );
  }, []);

  const handleSendOtp = useCallback(
    async (identifier: string, type: "email" | "phone") => {
      setIsLoading(true);
      setMessage("");
      setMessageType("info");
      setInvalidOtpAttempts(0);

      try {
        const response = await axiosInstance.post(
          App_Url_New.secured.backlinks.send_otp,
          buildOtpBody(identifier, type)
        );

        setMessage(
          response.data?.message ||
            (type === "email" ? "OTP sent to your email." : "OTP sent to your phone number.")
        );
        setMessageType("success");
      } catch (error: unknown) {
        setMessage(getApiErrorMessage(error, "Failed to send OTP."));
        setMessageType("error");
      } finally {
        setIsLoading(false);
      }
    },
    [getApiErrorMessage]
  );

  const handleVerifyOtp = useCallback(
    async (identifier: string, otp: string, type: "email" | "phone") => {
      setIsLoading(true);
      setMessage("");

      try {
        const verifyResponse = await axiosInstance.post(
          App_Url_New.secured.backlinks.verify_otp,
          buildOtpBody(identifier, type, otp)
        );

        if (verifyResponse?.status === 200) {
          dispatch(setCustomerAuth(verifyResponse.data));
        }

        const accessToken = verifyResponse.data?.access_token;
        const clientId = getClientIdFromVerifyResponse(verifyResponse.data, fallbackClientId);

        if (!clientId) {
          throw new Error("Client id not found for existing journey.");
        }

        const sessionData = await fetchResumeSession(clientId, accessToken);
        const resumeState = hydrateResumeJourneyState(sessionData);
        const userFirstName = (sessionData.client?.first_name || "").trim();
        const userLastName = (sessionData.client?.last_name || "").trim();
        const fullName = [userFirstName, userLastName].filter(Boolean).join(" ");

        dispatch(
          setRetailAuth({
            session: {
              client: {
                ...(sessionData.client || {}),
                id: String(sessionData.client?.id ?? sessionData.client_id ?? clientId),
              },
              session_data: sessionData,
            },
            accessToken,
          })
        );

        const welcomeMessage = fullName
          ? `Welcome back, ${fullName}. You have signed in successfully.`
          : "You have signed in successfully.";
        showToast(welcomeMessage, "success");
        setMessage(welcomeMessage);
        setMessageType("success");
        setInvalidOtpAttempts(0);

        await new Promise((resolve) => setTimeout(resolve, 900));

        if (onSessionResolved) {
          await onSessionResolved(sessionData, {
            target: resumeState.target,
            clientId,
            verifyResponse: verifyResponse.data,
          });
        } else if (showUserLoginForm) {
          onClose();
          navigate(App_Url_New.retail.frontlinks.retail_dashboard);
        } else if (autoRedirect) {
          // const targetPath =
          //   typeof target?.stepIndex === "number"
          //     ? `${target.path}?step=${target.stepIndex}`
          //     : target.path;
          // window.location.href = targetPath;
        }
      } catch (error: unknown) {
        const errorMessage = getApiErrorMessage(error, "Unable to verify OTP.");
        const isInvalidOtp = errorMessage.toLowerCase() === "invalid otp";
        const nextInvalidAttemptCount = isInvalidOtp ? invalidOtpAttempts + 1 : invalidOtpAttempts;

        if (isInvalidOtp) {
          setInvalidOtpAttempts(nextInvalidAttemptCount);
        }

        if (isInvalidOtp && nextInvalidAttemptCount >= MAX_INVALID_OTP_ATTEMPTS) {
          const lockedOutMessage =
            "Too many invalid OTP attempts. Redirecting you to the login page.";
          setMessage(lockedOutMessage);
          setMessageType("error");
          showToast(lockedOutMessage, "error");
          onClose();
          await new Promise((resolve) => setTimeout(resolve, 700));
          navigate(App_Url_New.secured.frontlinks.login, { replace: true });
        } else {
          const attemptsRemaining = isInvalidOtp
            ? MAX_INVALID_OTP_ATTEMPTS - nextInvalidAttemptCount
            : null;
          const finalErrorMessage =
            isInvalidOtp && attemptsRemaining !== null
              ? `${errorMessage}. ${attemptsRemaining} attempt${
                  attemptsRemaining === 1 ? "" : "s"
                } remaining.`
              : errorMessage;

          setMessage(finalErrorMessage);
          setMessageType("error");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      autoRedirect,
      dispatch,
      fallbackClientId,
      getApiErrorMessage,
      invalidOtpAttempts,
      navigate,
      onClose,
      onSessionResolved,
      showUserLoginForm,
    ]
  );

  const modalProps = useMemo(
    () => ({
      isOpen,
      onClose,
      onSendOtp: handleSendOtp,
      onVerifyOtp: handleVerifyOtp,
      isLoading,
    }),
    [handleSendOtp, handleVerifyOtp, isLoading, isOpen, onClose]
  );

  if (showUserLoginForm) {
    if (!isOpen) return null;

    if (renderInline) {
      return (
        <UserLoginForm
          {...modalProps}
          isOpen={true}
          errorMessage={messageType === "error" ? message : ""}
        />
      );
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-theme-text/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-lg mx-4">
          <UserLoginForm
            {...modalProps}
            isOpen={true}
            errorMessage={messageType === "error" ? message : ""}
          />
        </div>
      </div>
    );
  }

  return (
    <LoginModal
      {...modalProps}
      message={message}
      messageType={messageType}
      title={title}
      validationType={validationType}
      emailAddress={emailAddress}
      phoneNumber={phoneNumber}
      renderInline={renderInline}
    />
  );
};

export type { OtpResumeContext, OtpResumeLoginProps };
export default OtpResumeLogin;
